import type { DropdownListItem } from '@verantos-dev/shared-components';
import { ConceptGroup, type LegacyConceptGroup } from '@verantos-dev/types-concepts';

import {
  type AnalyticalVariableCriterion,
  type AnalyticalVariable,
} from '../cohort-dsl-schema/AnalyticalVariable';
import {
  CohortDefinitionSchema,
  type IndexDateCriterion,
} from '../cohort-dsl-schema/CohortDefinition';

export type CohortBuilderDefinitionLegacy = {
  /**
   * Array of subject variables.
   */
  simpleEvents: SimpleEventLegacy[];

  /**
   * Array of derived event variables.
   */
  derivedEvents: DerivedEventLegacy[];

  /**
   * The index date variable.
   */
  indexDate: IndexDateVariableLegacy | undefined;

  /**
   * Array of subject variables.
   */
  subjectVariables: SubjectVariableLegacy[];

  /**
   * Study period restrictions.
   */
  studyPeriodRestrictions: StudyPeriodRestrictionsLegacy | undefined;

  /**
   * Analytical variables for use in the cohort I/E criteria.
   *
   * FEATURE FLAG: Optional due to `analytical_variables_in_cohort_builder` feature flag.
   *
   * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag:
   * - Remove `?` to make this field required
   * - Update all usages to assume field is always present
   */
  analyticalVariables?: (AnalyticalVariable & {
    changeStatus?: CohortBuilderItemChangeStatusLegacy;
    changedBy?: CohortBuilderItemChangedBy;
  })[];

  /**
   * I/E criteria for the cohort, including subject variables and analytical variables.
   * PRIORITY: Always try to express criteria as AnalyticalVariableCriterion first.
   * Only use SubjectVariableCriterion if the logic cannot be expressed as an AnalyticalVariableCriterion.
   * Analytical variables provide more flexible and powerful logic for complex criteria.
   *
   * FEATURE FLAG: Optional due to `analytical_variables_in_cohort_builder` feature flag.
   *
   * CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag:
   * - Remove `?` to make this field required
   * - Update all usages to assume field is always present
   */
  criteria?: Criterion[];
};

export type StudyPeriodRestrictionsLegacy = {
  overallStudyPeriodRestriction: OverallStudyPeriodRestrictionLegacy | undefined;
  baselinePeriodRestriction: BaselinePeriodRestrictionLegacy | undefined;
  followUpPeriodRestriction: FollowUpPeriodRestrictionLegacy | undefined;
  changeStatus?: CohortBuilderItemChangeStatusLegacy | undefined;
  changedBy?: CohortBuilderItemChangedBy | undefined;
};

export type OverallStudyPeriodRestrictionLegacy = {
  isEnabled?: boolean;
  startDate: string;
  endDate: string;
};

export type BaselinePeriodRestrictionLegacy = {
  isEnabled?: boolean;
  daysBeforeIndexDate: number | undefined;
};

export type FollowUpPeriodRestrictionLegacy = {
  isEnabled?: boolean;
  daysAfterIndexDate: number | undefined;
};

export type IndexDateRangeLegacy = {
  startDate?: string;
  endDate?: string;
};

export type IndexDateEvent = {
  eventName: InputFieldLegacy;
  eventDateAttribute: InputFieldLegacy;
  occurrence: InputFieldLegacy;
  position?: InputFieldLegacy;
};

export type IndexDateVariableLegacy = {
  id: string;
  type: IndexDateTypeLegacy;
  // Legacy implementation of index date uses indexDateValue with reference to instance event name
  indexDateValue?: string;
  // Current implementation of index date uses indexDateEvent with reference to any event
  indexDateEvent?: IndexDateEvent;
  indexDateRangeValue?: IndexDateRangeLegacy;
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
  changedBy?: CohortBuilderItemChangedBy;
};

export type SubjectVariableLegacy = {
  id: string;
  name: string;
  type: SubjectVariableTypeLegacy | undefined;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  countFilter: FilterLegacy | undefined;
  timeWindow: TimeWindow | undefined;
  criteriaSelection: SubjectVariableCriteriaSelectionTypeLegacy;
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
  changedBy?: CohortBuilderItemChangedBy;
};

/**
 * Subject variable usage as I/E criteria in a cohort definition.
 * References a SubjectVariable by ID and acts as a criterion.
 */
export type SubjectVariableCriterionLegacy = {
  /**
   * Discriminator field for criteria union.
   */
  criteriaType: 'subject_variable';
  /**
   * ID of the subject variable reference.
   */
  subjectVariableId: string;
};

/**
 * Discriminated union for criteria (subject variables, analytical variables, and index date).
 * When generating cohort definitions, prioritize using AnalyticalVariableCriterion first.
 * Only use SubjectVariableCriterion if the logic cannot be expressed as an AnalyticalVariableCriterion.
 * IndexDateCriterion provides a new way to specify index dates using discriminated unions.
 */
export type Criterion =
  | SubjectVariableCriterionLegacy
  | AnalyticalVariableCriterion
  | IndexDateCriterion;

export type SimpleEventLegacy = {
  id: string;
  name: string;
  type: SimpleEventTypeLegacy | SimpleEventType | undefined;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  relatedEntityFilters: FilterLegacy[];
  uniquenessFilters: FilterLegacy[];
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
  changedBy?: CohortBuilderItemChangedBy;
};

export type DerivedEventLegacy = {
  id: string;
  name: string;
  type: DerivedEventTypeLegacy | undefined;
  anyOrder?: boolean;
  temporalConstraints: FilterLegacy[];
  eventFilters: DerivedEventFilterLegacy[];
  uniquenessFilters: FilterLegacy[];
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
  changedBy?: CohortBuilderItemChangedBy;
};

export type CohortBuilderEventLegacy = {
  eventName: InputFieldLegacy;
  eventDate: InputFieldLegacy;
  selected?: boolean;
};

export type FilterLegacy = {
  id: string;
  inputFields: InputFieldLegacy[];
  updated?: string;
};

export type InputFieldLegacy = {
  id: string;
  value: string | undefined;
  dirty?: boolean; // annotation indicating input field has been touched
};

export type InputFieldConfig = {
  inputType: InputFieldTypeLegacy;
  width: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number;
  label: string;
  options?: DropdownListItem[];
  placeholder?: string;
  helperText?: string;
  minValue?: number;
  minValueMsg?: string;
  maxValue?: number;
  maxValueMsg?: string;
  step?: number;
  disabled?: boolean;
};

export type DerivedEventFilterLegacy = {
  id: string; // id of this event filter occurrence
  eventName: InputFieldLegacy; // name of the event that is selected
  eventDateAttribute: InputFieldLegacy;
  isSelected?: InputFieldLegacy; // only for sequence events
  position?: InputFieldLegacy; // only for instance events
  index?: InputFieldLegacy; // only for instance events
};

export type TimeWindow = CohortDefinitionSchema['subjectVariables'][number]['timeWindow'];

export enum CohortBuilderItemChangeStatusLegacy {
  NEW = 'NEW',
  EDITED = 'EDITED',
  UNCHANGED = 'UNCHANGED',
}

export enum CohortBuilderItemChangedBy {
  COHORT_BUILDER = 'COHORT_BUILDER',
  RESEARCH_ASSISTANT = 'RESEARCH_ASSISTANT',
}

export enum CohortBuilderItemStatus {
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum DerivedEventTypeLegacy {
  MERGED = 'Merged',
  SEQUENCE = 'Sequence',
  INSTANCE = 'Instance',
}

export enum SimpleEventTypeLegacy {
  CONDITION_OCCURRENCE = 'Condition occurrence',
  DRUG_EXPOSURE = 'Drug exposure',
  MEASUREMENT = 'Measurement',
  OBSERVATION = 'Observation',
  PROCEDURE_OCCURRENCE = 'Procedure occurrence',
  VISIT_OCCURRENCE = 'Visit occurrence',
}

export enum SubjectVariableTypeLegacy {
  DEMOGRAPHICS = 'Demographics',
  COUNT = 'Count',
}

export enum SubjectVariableCriteriaSelectionTypeLegacy {
  INCLUSION = 'Inclusion',
  EXCLUSION = 'Exclusion',
  NOT_APPLIED = 'Not applied',
}

export enum IndexDateTypeLegacy {
  EVENT = 'Event instance',
  CALENDAR = 'Calendar date',
}

export enum UniquenessFilterTypesEnumLegacy {
  FIXED_WINDOW = 'FIXED_WINDOW',
  ROLLING_WINDOW = 'ROLLING_WINDOW',
}

export enum UniquenessFilterTimeUnitEnumLegacy {
  DAYS = 'DAYS',
}

export enum UniquenessFilterDateAttributesEnumLegacy {
  DERIVED_EVENT_DATE = 'derived_event_date',
}

export enum CohortBuilderStepLegacy {
  EVENTS = 'Events',
  VARIABLES = 'Variables',
  DEFINITION = 'Definition',
  SUBJECTSV2 = 'SubjectsV2',
  CHARACTERISTICS = 'Characteristics',
  SUMMARY = 'Summary',
}

export enum CohortBuilderModalLegacy {
  CONFIGURE_STUDY_PERIOD,
  ADD_INDEX_DATE,
  EDIT_INDEX_DATE,
  ADD_SUBJECT_VARIABLE,
  EDIT_SUBJECT_VARIABLE,
  ADD_SIMPLE_EVENT,
  EDIT_SIMPLE_EVENT,
  ADD_DERIVED_EVENT,
  EDIT_DERIVED_EVENT,
}

export enum CohortActionModalLegacy {
  EXIT_COHORT,
  SAVE_COHORT,
  PROCESS_COHORT,
}

export enum FilterTypeLegacy {
  CONCEPT,
  NUMERIC,
  UNIQUENESS,
  RELATED_ENTITY,
  TEMPORAL,
  COUNT,
  EVENT,
}

export enum SimpleEventFilterType {
  CONCEPT,
  NUMERIC,
  UNIQUENESS,
  RELATED_ENTITY,
}

export const TimeWindowDateTypeEnum = {
  RELATIVE_TO_INDEX_DATE: 'RELATIVE_TO_INDEX_DATE',
  ABSOLUTE_DATE: 'ABSOLUTE_DATE',
  START_END_OF_DATA: 'START_END_OF_DATA',
};

export const TimeWindowRelativeDateDirectionEnum = {
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
};

export enum InputFieldTypeLegacy {
  COMBO_BOX = 'COMBO_BOX',
  CONCEPT_COMBO_BOX = 'CONCEPT_COMBO_BOX',
  DATE_PICKER = 'DATE_PICKER',
  DROPDOWN = 'DROPDOWN',
  FLOAT_NUMERIC_FIELD = 'FLOAT_NUMERIC_FIELD',
  INT_NUMERIC_FIELD = 'INT_NUMERIC_FIELD',
  TEXT_FIELD = 'TEXT_FIELD',
  CHECKBOX = 'CHECKBOX',
}

export enum DerivedEventInputFieldTypeLegacy {
  EVENT_NAME,
  EVENT_DATE_ATTRIBUTE,
  EVENT_IS_SELECTED,
  EVENT_POSITION,
  EVENT_INDEX,
}

export enum ConceptAttributesEnumLegacy {
  GENDER_CONCEPT = 'gender_concept',
  RACE_CONCEPT = 'race_concept',
  ETHNICITY_CONCEPT = 'ethnicity_concept',
}

export enum ComparatorOperatorsEnumLegacy {
  IN = 'IN',
  EQUAL = '=',
  NOT_EQUAL = '!=',
  SMALLER_THAN = '<',
  GREATER_THAN = '>',
  SMALLER_OR_EQUAL = '<=',
  GREATER_OR_EQUAL = '>=',
}

export enum NumericFiltersAttributesEnumLegacy {
  YEAR_OF_BIRTH = 'year_of_birth',
  MONTH_OF_BIRTH = 'month_of_birth',
  DAY_OF_BIRTH = 'day_of_birth',
  AGE_AT_INDEX = 'AGE_AT_INDEX',
}

export enum ConceptAttributesConditionOccurrenceEnumLegacy {
  CONDITION_CONCEPT = 'condition_concept',
  CONDITION_TYPE_CONCEPT = 'condition_type_concept',
  CONDITION_SOURCE_CONCEPT = 'condition_source_concept',
  CONDITION_STATUS_CONCEPT = 'condition_status_concept',
}

export enum ConceptAttributesDrugExposureEnumLegacy {
  DRUG_CONCEPT = 'drug_concept',
  DRUG_TYPE_CONCEPT = 'drug_type_concept',
  ROUTE_CONCEPT = 'route_concept',
  DRUG_SOURCE_CONCEPT = 'drug_source_concept',
}

export enum ConceptAttributesMeasurementEnumLegacy {
  MEASUREMENT_CONCEPT = 'measurement_concept',
  MEASUREMENT_TYPE_CONCEPT = 'measurement_type_concept',
  OPERATOR_CONCEPT = 'operator_concept',
  VALUE_AS_NUMBER = 'value_as_number',
  VALUE_AS_CONCEPT = 'value_as_concept',
  UNIT_CONCEPT = 'unit_concept',
}

export enum ConceptAttributesObservationEnumLegacy {
  OBSERVATION_CONCEPT = 'observation_concept',
  OBSERVATION_TYPE_CONCEPT = 'observation_type_concept',
  VALUE_AS_NUMBER = 'value_as_number',
  VALUE_AS_CONCEPT = 'value_as_concept',
  UNIT_CONCEPT = 'unit_concept',
}

export enum ConceptAttributesProcedureOccurrenceEnumLegacy {
  PROCEDURE_CONCEPT = 'procedure_concept',
  PROCEDURE_TYPE_CONCEPT = 'procedure_type_concept',
  QUALIFIER_CONCEPT = 'qualifier_concept',
}

export enum ConceptAttributesVisitOccurrenceEnumLegacy {
  VISIT_CONCEPT = 'visit_concept',
  VISIT_TYPE_CONCEPT = 'visit_type_concept',
  VISIT_SOURCE_CONCEPT = 'visit_source_concept',
}

export enum NumericFiltersAttributesDrugExposureEnumLegacy {
  DAYS_SUPPLY = 'days_supply',
  REFILLS = 'refills',
  QUANTITY = 'quantity',
}

export enum NumericFiltersAttributesMeasurementEnumLegacy {
  RANGE_LOW = 'range_low',
  RANGE_HIGH = 'range_high',
  VALUE_AS_NUMBER = 'value_as_number',
}

export enum NumericFiltersAttributesObservationEnumLegacy {
  VALUE_AS_NUMBER = 'value_as_number',
}

export enum NumericFiltersAttributesProcedureOccurrenceEnumLegacy {
  QUANTITY = 'quantity',
}

// below are sort for each event type and filter

// CO = Concept Occurrence
// DE = Drug Exposure
// ME = Measurement
// OB = Observation
// PO = Procedure Occurrence
// VO = Visit Occurrence

// UF = Uniqueness Filter

export enum CO_UF_DateAttributesEnumLegacy {
  CONDITION_START_DATE = 'condition_start_date',
  CONDITION_END_DATE = 'condition_end_date',
  VISIT_START_DATE = 'visit_start_date',
  VISIT_END_DATE = 'visit_end_date',
}

export enum DE_UF_DateAttributesEnumLegacy {
  DRUG_EXPOSURE_START_DATE = 'drug_exposure_start_date',
  DRUG_EXPOSURE_END_DATE = 'drug_exposure_end_date',
}
export enum ME_UF_DateAttributesEnumLegacy {
  MEASUREMENT_DATE = 'measurement_date',
}

export enum OB_UF_DateAttributesEnumLegacy {
  OBSERVATION_DATE = 'observation_date',
}
export enum PO_UF_DateAttributesEnumLegacy {
  PROCEDURE_DATE = 'procedure_date',
}

export enum VO_UF_DateAttributesEnumLegacy {
  VISIT_START_DATE = 'visit_start_date',
  VISIT_END_DATE = 'visit_end_date',
}

// RE = Related Entity Filter

export enum RE_EntityFilterEnumLegacy {
  VISIT_OCCURRENCE = 'VISIT_OCCURRENCE',
  CONDITION_OCCURRENCE = 'CONDITION_OCCURRENCE',
  PROVIDER = 'PROVIDER',
}
export enum RE_AttributesEnumLegacy {
  VISIT_CONCEPT = 'visit_concept',
  VISIT_TYPE_CONCEPT = 'visit_type_concept',
  VISIT_SOURCE_CONCEPT = 'visit_source_concept',
  SPECIALTY_CONCEPT = 'specialty_concept',
  CONDITION_CONCEPT = 'condition_concept',
  CONDITION_TYPE_CONCEPT = 'condition_type_concept',
  CONDITION_STATUS_CONCEPT = 'condition_status_concept',
  CONDITION_SOURCE_CONCEPT = 'condition_source_concept',
}

export enum TemporalConstraintsEnumLegacy {
  MINIMUM_BETWEEN = 'MINIMUM_BETWEEN',
  MAXIMUM_BETWEEN = 'MAXIMUM_BETWEEN',
  TOTAL_BETWEEN = 'TOTAL_BETWEEN',
  DAYS = 'DAYS',
}

export enum InstanceEventEnumLegacy {
  CONDITION_START_DATE = 'condition_start_date',
}

export enum IndexDateEventOccurrenceEnum {
  FIRST = 'FIRST',
  LAST = 'LAST',
  NTH = 'NTH',
}

export type InputOptionContextDataLegacy = {
  conceptGroups?: LegacyConceptGroup[] | ConceptGroup[];
  eventNames?: DropdownListItem[];
  eventFilterNames?: string[];
  nextDerivedEventFilters?: DerivedEventFilterLegacy[];
  currentCohortDefinition?: CohortBuilderDefinitionLegacy;
  activeCohortBuilderItem?: ActiveCohortBuilderItemLegacy;
  derivedEventType?: DerivedEventTypeLegacy;
  currentFilter?: FilterLegacy;
  featureFlags?: Record<string, string | boolean>;
};

export type ActiveCohortBuilderItemLegacy =
  | ActiveSubjectsVariableItemLegacy
  | ActiveSimpleEventItemLegacy
  | ActiveDerivedEventItemLegacy
  | ActiveIndexDateItemLegacy;

export type ActiveIndexDateItemLegacy = {
  type: IndexDateTypeLegacy | undefined;
  // Legacy implementation of index date uses indexDateValue with reference to instance event name
  indexDateValue?: string;
  // Current implementation of index date uses indexDateEvent with reference to any event
  indexDateEvent?: IndexDateEvent;
  indexDateRangeValue?: IndexDateRangeLegacy;
} & CohortBuilderItemBase;

export type ActiveSubjectsVariableItemLegacy = {
  type: SubjectVariableTypeLegacy | undefined;
} & CohortBuilderItemBase;

export type ActiveSimpleEventItemLegacy = {
  // adding SimpleEventType for cohort builder dsl
  type: SimpleEventTypeLegacy | SimpleEventType | undefined;
} & CohortBuilderItemBase;

export type ActiveDerivedEventItemLegacy = {
  type: DerivedEventTypeLegacy | undefined;
} & CohortBuilderItemBase;

type CohortBuilderItemBase = {
  id: string;
  name: string;
  anyOrder?: boolean;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  relatedEntityFilters?: FilterLegacy[];
  uniquenessFilters?: FilterLegacy[];
  temporalConstraints: FilterLegacy[];
  eventFilters: DerivedEventFilterLegacy[];
  countFilter: FilterLegacy | undefined;
  inputFieldConfigMap: Record<string, InputFieldConfig>;
  timeWindow: TimeWindow;
};

export enum GrammarDerivedEventTypeLegacy {
  MERGED = 'MERGED_EVENT',
  SEQUENCE = 'SEQUENCE_EVENT',
  INSTANCE = 'INSTANCE_EVENT',
}

export enum GrammarSimpleEventTypeLegacy {
  CONDITION_OCCURRENCE = 'CONDITION_OCCURRENCE',
  DRUG_EXPOSURE = 'DRUG_EXPOSURE',
  MEASUREMENT = 'MEASUREMENT',
  OBSERVATION = 'OBSERVATION',
  PROCEDURE_OCCURRENCE = 'PROCEDURE_OCCURRENCE',
  VISIT_OCCURRENCE = 'VISIT_OCCURRENCE',
}

// json dsl
export enum SimpleEventType {
  CONDITION_OCCURRENCE = 'condition_occurrence',
  DRUG_EXPOSURE = 'drug_exposure',
  MEASUREMENT = 'measurement',
  OBSERVATION = 'observation',
  PROCEDURE_OCCURRENCE = 'procedure_occurrence',
  VISIT_OCCURRENCE = 'visit_occurrence',
}

export enum Entity {
  CONDITION_OCCURRENCE = 'condition_occurrence',
  DRUG_EXPOSURE = 'drug_exposure',
  MEASUREMENT = 'measurement',
  OBSERVATION = 'observation',
  PROCEDURE_OCCURRENCE = 'procedure_occurrence',
  VISIT_OCCURRENCE = 'visit_occurrence',
  PROVIDER = 'provider',
}

export enum Attribute {
  CONDITION_CONCEPT = 'condition_concept',
  CONDITION_TYPE_CONCEPT = 'condition_type_concept',
  CONDITION_STATUS_CONCEPT = 'condition_status_concept',
  CONDITION_SOURCE_CONCEPT = 'condition_source_concept',
  CONDITION_START_DATE = 'condition_start_date',
  CONDITION_END_DATE = 'condition_end_date',
  VISIT_CONCEPT = 'visit_concept',
  VISIT_SOURCE_CONCEPT = 'visit_source_concept',
  VISIT_TYPE_CONCEPT = 'visit_type_concept',
  SPECIALTY_CONCEPT = 'specialty_concept',
  DRUG_CONCEPT = 'drug_concept',
  DRUG_TYPE_CONCEPT = 'drug_type_concept',
  DRUG_SOURCE_CONCEPT = 'drug_source_concept',
  ROUTE_CONCEPT = 'route_concept',
  REFILLS = 'refills',
  DAYS_SUPPLY = 'days_supply',
  QUANTITY = 'quantity',
  DRUG_EXPOSURE_START_DATE = 'drug_exposure_start_date',
  DRUG_EXPOSURE_END_DATE = 'drug_exposure_end_date',
  MEASUREMENT_CONCEPT = 'measurement_concept',
  MEASUREMENT_TYPE_CONCEPT = 'measurement_type_concept',
  MEASUREMENT_SOURCE_CONCEPT = 'measurement_source_concept',
  UNIT_CONCEPT = 'unit_concept',
  VALUE_AS_CONCEPT = 'value_as_concept',
  MEASUREMENT_DATE = 'measurement_date',
  RANGE_HIGH = 'range_high',
  RANGE_LOW = 'range_low',
  VALUE_AS_NUMBER = 'value_as_number',
  OBSERVATION_CONCEPT = 'observation_concept',
  OBSERVATION_TYPE_CONCEPT = 'observation_type_concept',
  OBSERVATION_DATE = 'observation_date',
  PROCEDURE_CONCEPT = 'procedure_concept',
  PROCEDURE_TYPE_CONCEPT = 'procedure_type_concept',
  PROCEDURE_DATE = 'procedure_date',
  VISIT_START_DATE = 'visit_start_date',
  VISIT_END_DATE = 'visit_end_date',
  UNIT_SOURCE_CONCEPT = 'unit_source_concept',
  GENDER_CONCEPT = 'gender_concept',
  RACE_CONCEPT = 'race_concept',
  ETHNICITY_CONCEPT = 'ethnicity_concept',
  MODIFIER_CONCEPT = 'modifier_concept',
  PLACE_OF_SERVICE_CONCEPT = 'place_of_service_concept',
  DERIVED_EVENT_DATE = 'derived_event_date',
}

export enum ConceptDomain {
  GENDER = 'Gender',
  RACE = 'Race',
  ETHNICITY = 'Ethnicity',
  CONDITION = 'Condition',
  TYPE_CONCEPT = 'Type Concept',
  CONDITION_STATUS = 'Condition Status',
  DRUG = 'Drug',
  ROUTE = 'Route',
  MEASUREMENT = 'Measurement',
  MEAS_VALUE = 'Meas Value',
  UNIT = 'Unit',
  DEVICE = 'Device',
  OBSERVATION = 'Observation',
  PROCEDURE = 'Procedure',
  SPEC_ANATOMIC_SITE = 'Spec Anatomic Site',
  VISIT = 'Visit',
  PROVIDER = 'Provider',
  MODIFIER = 'Modifier',
  PLACE_OF_SERVICE = 'Place of Service',
}

export enum DerivedEventType {
  MERGED_EVENT = 'merged_event',
  INSTANCE_EVENT = 'instance_event',
  SEQUENCE_EVENT = 'sequence_event',
}

export enum DerivedEventFilterType {
  EVENT = 'EVENT',
  UNIQUENESS = 'UNIQUENESS',
  TEMPORAL = 'TEMPORAL',
}

export enum SubjectVariableFilterType {
  CONCEPT = 'CONCEPT',
  NUMERIC = 'NUMERIC',
  COUNT = 'COUNT',
}
